import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { BASE_URL } from "../config/apiconfig";

const AddressContext = createContext(null);

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error("useAddress must be used within AddressProvider");
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  // Enhanced token synchronization
  useEffect(() => {
    const handleStorage = () => {
      const newToken = localStorage.getItem("token");
      setToken(newToken);
    };

    // Listen for storage changes
    window.addEventListener("storage", handleStorage);

    // Also check for token changes periodically (for same-tab updates)
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== token) {
        setToken(currentToken);
      }
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(tokenCheckInterval);
    };
  }, [token]);

  // Clear addresses when token is removed (logout)
  useEffect(() => {
    if (!token) {
      setAddresses([]);
      setError(null);
    }
  }, [token]);

  // Fetch addresses with improved error handling
  const getAddresses = useCallback(
    async (userId) => {
      if (!userId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {

        const res = await axios.get(
          `${BASE_URL}/user-address/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );


        if (res.data?.status === 200) {
          const fetchedAddresses = res.data.data || [];
          setAddresses(fetchedAddresses);
          setError(null); // Clear any previous errors
        } else {
          setError("Failed to fetch addresses");
          setAddresses([]); // Clear addresses on error
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Something went wrong";
        setError(errorMessage);

        // Clear addresses on error
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Add / Update address with improved error handling
  const saveAddress = useCallback(
    async (addressData, addressId = null) => {
      // Get userId from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (!userId || !token) {
        return { success: false, message: "Unauthorized" };
      }

      try {
        const url = addressId
          ? `${BASE_URL}/user-address/${addressId}`
          : `${BASE_URL}/user-address`;

        const method = addressId ? "put" : "post";


        const res = await axios({
          url,
          method,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            ...addressData,
            user_id: userId,
          },
        });


        // Refresh list after successful save
        await getAddresses(userId);

        return { success: true, data: res.data };
      } catch (err) {
        return {
          success: false,
          message: err.response?.data?.message || "Save failed",
        };
      }
    },
    [token, getAddresses]
  );

  // Set primary address with improved error handling
  const setPrimaryAddress = useCallback(
    async (userId, addressId) => {
      if (!userId || !token) {
        return { success: false, message: "Unauthorized" };
      }

      try {

        const res = await axios.put(
          `${BASE_URL}/user-address/${addressId}`,
          {
            is_primary: "1",
            update_audit_id: userId
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );


        // Refresh addresses after marking primary
        await getAddresses(userId);

        return { success: true, data: res.data };
      } catch (err) {
        return {
          success: false,
          message: err.response?.data?.message || "Failed to set primary",
        };
      }
    },
    [token, getAddresses]
  );

  // Delete address with improved error handling
  const deleteAddress = useCallback(
    async (userId, addressId) => {
      if (!userId || !token) {
        return { success: false, message: "Unauthorized" };
      }

      try {

        await axios.delete(`${BASE_URL}/user-address/${addressId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            delete_audit_id: userId
          },
        });


        // Refresh addresses after deletion
        await getAddresses(userId);

        return { success: true };
      } catch (err) {

        return {
          success: false,
          message: err.response?.data?.message || "Delete failed",
        };
      }
    },
    [token, getAddresses]
  );

  return (
    <AddressContext.Provider
      value={{
        addresses,
        loading,
        error,
        getAddresses,
        saveAddress,
        deleteAddress,
        setAddresses,
        setPrimaryAddress,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};
